import { type QueryClient } from '@tanstack/react-query'
import { createContext, data as routerData, type MiddlewareFunction } from 'react-router'

import { getDashboardEmployers, getGetDashboardEmployersQueryKey } from 'utils/web_api'
import { type DashboardEmployersContext } from 'utils/web_api.schemas'

export const clientContext = createContext<QueryClient | null>(null)
export const dashboardContext = createContext<DashboardEmployersContext | null>(null)

function getRequestIdFromError (error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'requestId' in error && typeof error.requestId === 'string') {
    return error.requestId
  }

  return undefined
}

function getStatusFromError (error: unknown): number {
  if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
    return error.status
  }

  return 500
}

function getMessageFromError (error: unknown): string {
  let message = ''

  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    message = error.message
  }

  return message || 'Something unexpected happened. We have been notified and are looking into it.'
}

export function createDashboardContextMiddleware (queryClient: QueryClient) {
  const middleware: MiddlewareFunction = async ({ context }, next) => {
    context.set(clientContext, queryClient)

    try {
      const data = await queryClient.ensureQueryData({
        queryKey: getGetDashboardEmployersQueryKey(),
        queryFn: getDashboardEmployers,
      })

      context.set(dashboardContext, data)

      await next()
    } catch (error) {
      const requestId = getRequestIdFromError(error)
      const status = getStatusFromError(error)
      const message = getMessageFromError(error)

      throw routerData({ requestId }, { status, statusText: message })
    }
  }

  return middleware
}
